/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { WS_BASE_URL } from "../api/config";

const DEFAULT_RETRY_MS = [1000, 2000, 5000, 10000, 20000, 30000];

export class NotificationsWSClient {
  constructor({ onReady, onNotification, onError } = {}) {
    this.onReady = onReady || (() => {});
    this.onNotification = onNotification || (() => {});
    this.onError = onError || (() => {});

    this.ws = null;
    this.closedManually = false;
    this.retryIndex = 0;
  }

  _buildUrl() {
    const base = WS_BASE_URL || (() => {
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      return `${protocol}://${window.location.host}`;
    })();
    const token = localStorage.getItem("token");
    const qp = token ? `?token=${encodeURIComponent(token)}` : "";
    return `${base}/ws/notifications/${qp}`;
  }

  connect() {
    this.closedManually = false;
    const token = localStorage.getItem("token");
    if (!token) {
      // Do not attempt to connect without a token
      return;
    }
    const url = this._buildUrl();
    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      // Swallow construction errors, schedule reconnect
      // Use the error to satisfy no-unused-vars
      String(err);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.retryIndex = 0; // reset backoff after a successful connect
    };

    this.ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg?.type === "notification_ready") {
          this.onReady();
        } else if (msg?.type === "notification" && msg?.data) {
          this.onNotification(msg.data);
        }
      } catch (err) {
        // Ignore JSON parse or handler errors
        String(err);
      }
    };

    this.ws.onerror = (err) => {
      this.onError(err);
    };

    this.ws.onclose = () => {
      if (!this.closedManually) this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    const delay = DEFAULT_RETRY_MS[Math.min(this.retryIndex, DEFAULT_RETRY_MS.length - 1)];
    this.retryIndex += 1;
    setTimeout(() => {
      // Re-read token on each reconnect attempt
      this.connect();
    }, delay);
  }

  close() {
    this.closedManually = true;
    try {
      if (this.ws) this.ws.close();
    } catch (err) {
      // Ignore close errors
      String(err);
    }
    this.ws = null;
  }
}
